import type { ComponentProps } from "react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { jt, t } from "ttag";

import { PricingCard } from "@/app/components/legacy-components";
import type { Plan } from "@/features/billing/models";
import { getMonthsDurationText } from "@/features/billing/utils";
import {
  Accordion,
  AltArrowDownIcon,
  Badge,
  checkableStyles,
  ClockIcon,
  Label,
  MarkdownContent,
  RadioGroup,
  ShowcaseCard,
  StarIcon,
} from "@/shared/ui";

type SubscriptionItemProps = ComponentProps<typeof RadioGroup.Item> & {
  plan: Plan;
  isPendingPlan?: boolean;
  isCurrentPlan?: boolean;
};

const showcaseCardClassName = checkableStyles().itemRoot({
  isCard: true,
  className: "max-w-subscription-card p-0",
});

export const SubscriptionItem = ({
  plan,
  isCurrentPlan,
  isPendingPlan,
  ...props
}: SubscriptionItemProps) => {
  const isMobile = useMediaQuery("(max-width: 847px)");

  const hasDiscountWithDuration = Boolean(
    plan.discount?.isValid && plan.discount.durationInMonths,
  );

  const durationInMonthsPeriod =
    plan.discount?.isValid && plan.discount.durationInMonths
      ? jt`for ${plan.discount.durationInMonths} month(s)`
      : "";

  const durationInMonths =
    plan.discount?.isValid && plan.discount.durationInMonths
      ? `${plan.discount.durationInMonths} `
      : "";

  const monthsDuration = getMonthsDurationText(plan.durationInMonths);

  const planPrice = (
    <strong key={`${plan.subscriptionPriceId}-initial-price`}>
      ${plan.price}
    </strong>
  );

  return (
    <ShowcaseCard.Root className={showcaseCardClassName}>
      <PricingCard.Thumbnail
        src={plan.thumbnail}
        alt={`${plan.name} Thumbnail`}
      />

      <PricingCard.Container>
        <PricingCard.BadgeContainer>
          {isPendingPlan && (
            <Badge color="yellow" size="small">
              <ClockIcon className="size-3" />
              {t`Pending`}
            </Badge>
          )}

          {isCurrentPlan && (
            <Badge color="dark-secondary" size="small">
              <StarIcon className="size-3" />
              {t`Current subscription`}
            </Badge>
          )}
        </PricingCard.BadgeContainer>

        <PricingCard.Subtitle>
          {plan.isCompound && t`Compounded`}
        </PricingCard.Subtitle>

        <div className="flex w-full justify-between gap-4">
          <PricingCard.Title>{plan.name}</PricingCard.Title>

          <RadioGroup.Item {...props} />
        </div>

        <PricingCard.Description>
          <MarkdownContent>{plan.description}</MarkdownContent>
        </PricingCard.Description>

        <p>
          {plan.discount && (
            <PricingCard.PreviousPrice>${plan.price}</PricingCard.PreviousPrice>
          )}

          <PricingCard.Price>
            $
            {plan.discount?.isValid
              ? plan.discount.priceAfterDiscount
              : plan.price}
          </PricingCard.Price>

          <PricingCard.PriceDetails
            hasDurationInMonths={hasDiscountWithDuration}
          >
            {" "}
            / {monthsDuration} {durationInMonthsPeriod}
          </PricingCard.PriceDetails>
        </p>

        <PricingCard.Disclaimer hasDiscount={Boolean(plan.discount?.isValid)}>
          {plan.disclaimer}
        </PricingCard.Disclaimer>

        {plan.discount && (
          <PricingCard.DiscountAlert>
            {jt`After the first ${durationInMonths}month(s), your subscription will change to ${planPrice} every ${monthsDuration}`}
          </PricingCard.DiscountAlert>
        )}

        <PricingCard.Footer className="w-full">
          {isMobile && (
            <Accordion.Root type="single" collapsible className="w-full">
              <Accordion.Item value="features">
                <Accordion.Trigger className="w-full justify-between [&[data-state=open]>svg]:rotate-180">
                  <Label
                    size="sm"
                    htmlFor={`${plan.subscriptionPriceId}-features`}
                    className="cursor-pointer font-semibold text-neutral-600"
                  >{t`What you need to know`}</Label>

                  <AltArrowDownIcon className="size-4 transition-transform duration-200" />
                </Accordion.Trigger>

                <Accordion.Content className="mt-1">
                  <PricingCard.FeaturesList>
                    <MarkdownContent>{plan.features}</MarkdownContent>
                  </PricingCard.FeaturesList>
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          )}

          {!isMobile && (
            <>
              <Label
                size="sm"
                htmlFor={`${plan.subscriptionPriceId}-features`}
                className="font-semibold text-neutral-600"
              >{t`What you need to know`}</Label>

              <PricingCard.FeaturesList>
                <MarkdownContent>{plan.features}</MarkdownContent>
              </PricingCard.FeaturesList>
            </>
          )}
        </PricingCard.Footer>
      </PricingCard.Container>
    </ShowcaseCard.Root>
  );
};
SubscriptionItem.displayName = "SubscriptionItem.Item";
